import { useContext } from 'react';

import { Calendar as ReactBigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { useNavigate } from 'react-router-dom';
import styled, { ThemeContext } from 'styled-components';
import { format, getDay, getISOWeek, parse, startOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

import 'react-big-calendar/lib/css/react-big-calendar.css';

// Cast pour compatibilité @types/react-big-calendar vs React 18 (refs legacy)
const Calendar = ReactBigCalendar as unknown as React.JSX.ElementType;

import { dateFromString } from 'helpers/dateTimes';

import { SupplyOrderCalendarEvent } from './components/SupplyOrderCalendarEvent';
import { getColorsFromState } from './constants/states';
import type { SupplyOrderRow } from './constants/supplyOrderViews';

const locales = { fr };

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

type SupplyOrderEvent = {
    id: string;
    title: string;
    start: Date;
    end: Date;
    allDay: boolean;
    supplyOrder: SupplyOrderRow;
    backgroundColor: string;
};

/**
 * Un achat sans date de livraison prévue ni date de commande n'a pas sa place sur un calendrier :
 * il n'apparaît pas ici, mais reste visible dans le kanban et le tableau.
 */
function mapSupplyOrderToEvent(supplyOrder: SupplyOrderRow): SupplyOrderEvent | null {
    const dateString = supplyOrder.dateDeliveryScheduled || supplyOrder.orderDate;
    if (!dateString) {
        return null;
    }

    const date = dateFromString(dateString);
    const { backgroundColor } = getColorsFromState(
        supplyOrder.state,
        supplyOrder.weeklyMenusStartDate,
        supplyOrder.isMerged,
    );

    return {
        id: supplyOrder._id,
        title: `${supplyOrder.supplier.name} #${supplyOrder.number || '?'}`,
        start: date,
        end: date,
        allDay: true,
        supplyOrder,
        backgroundColor,
    };
}

type SupplyOrdersTimelineViewProps = {
    supplyOrders: SupplyOrderRow[];
    selectedSupplyOrderIds: string[];
    toggleSupplyOrderSelection: (supplyOrderId: string) => void;
};

export const SupplyOrdersTimelineView = ({
    supplyOrders,
    selectedSupplyOrderIds,
    toggleSupplyOrderSelection,
}: SupplyOrdersTimelineViewProps) => {
    const navigate = useNavigate();
    const theme = useContext(ThemeContext);

    const events = supplyOrders.map(mapSupplyOrderToEvent).filter((event): event is SupplyOrderEvent => event !== null);

    const components = {
        event: ({ event }: { event: SupplyOrderEvent }) => (
            <SupplyOrderCalendarEvent
                supplyOrder={event.supplyOrder}
                isSelected={selectedSupplyOrderIds.includes(event.id)}
            />
        ),
    };

    return (
        <Content>
            <CalendarContainer>
                <Calendar
                    localizer={localizer}
                    events={events}
                    eventPropGetter={() => ({ style: { backgroundColor: 'transparent' } })}
                    dayPropGetter={(date: Date) => {
                        const today = new Date();
                        const isToday =
                            date.getDate() === today.getDate() &&
                            date.getMonth() === today.getMonth() &&
                            date.getFullYear() === today.getFullYear();
                        return {
                            style: {
                                backgroundColor: isToday ? theme?.backgroundColor : theme?.cardBackgroundColor,
                            },
                        };
                    }}
                    startAccessor="start"
                    endAccessor="end"
                    views={['month', 'week']}
                    defaultView="week"
                    formats={{
                        dayFormat: (date: Date) => format(date, 'EEEE dd MMM', { locale: fr }),
                        dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
                            `S${getISOWeek(start)} ${format(start, 'dd MMM', { locale: fr })} – ${format(end, 'dd MMM', { locale: fr })}`,
                    }}
                    components={components}
                    onSelectEvent={(event: SupplyOrderEvent) => toggleSupplyOrderSelection(event.id)}
                    onDoubleClickEvent={(event: SupplyOrderEvent) => navigate(`/supply-order/${event.id}`)}
                    style={{ height: '100%', width: '100%' }}
                    culture="fr"
                    messages={{
                        previous: 'Précédent',
                        next: 'Suivant',
                        today: "Aujourd'hui",
                        showMore: (total: number) => `+${total} de plus`,
                        week: 'Semaine',
                        month: 'Mois',
                    }}
                />
            </CalendarContainer>
        </Content>
    );
};

const CalendarContainer = styled.div`
    flex: 1;
    overflow: auto;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    padding: 10px;

    .rbc-event {
        padding: 0;
        margin: 1px 0;
        background-color: transparent;
        border: none;
    }

    .rbc-event:focus {
        outline: none;
    }

    .rbc-show-more {
        font-size: 12px;
        font-weight: 600;
    }

    .rbc-time-view {
        display: flex;
        flex-direction: column;
        flex: 1;
    }

    .rbc-time-content {
        display: none;
    }

    .rbc-time-header {
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    .rbc-time-header-content {
        border: none;
        flex: 1;
        display: flex;
        flex-direction: column;
    }

    .rbc-allday-cell {
        flex: 1;
        overflow-y: auto;
    }

    .rbc-row-content {
        min-height: 100%;
    }

    .rbc-toolbar {
        color: ${({ theme }) => theme.textColor};
    }

    .rbc-toolbar button {
        color: ${({ theme }) => theme.textColor};
        border-color: ${({ theme }) => theme.lightBorderColor};
    }

    .rbc-toolbar button:hover,
    .rbc-toolbar button:focus {
        background-color: ${({ theme }) => theme.backgroundColor};
        color: ${({ theme }) => theme.textColor};
    }

    .rbc-toolbar button.rbc-active {
        background-color: ${({ theme }) => theme.ctaPrimaryColor};
        color: ${({ theme }) => theme.ctaPrimaryTextColor};
        border-color: ${({ theme }) => theme.ctaPrimaryColor};
    }

    .rbc-header {
        color: ${({ theme }) => theme.textColor};
        border-color: ${({ theme }) => theme.lightBorderColor};
        padding: 8px 4px;
        font-weight: 600;
    }

    .rbc-month-view,
    .rbc-time-view {
        border-color: ${({ theme }) => theme.lightBorderColor};
    }

    .rbc-day-bg + .rbc-day-bg,
    .rbc-month-row + .rbc-month-row {
        border-color: ${({ theme }) => theme.lightBorderColor};
    }

    .rbc-off-range-bg {
        background-color: ${({ theme }) => theme.backgroundColor};
    }

    .rbc-date-cell {
        color: ${({ theme }) => theme.textColor};
        padding: 4px 8px;
    }

    .rbc-off-range {
        color: ${({ theme }) => theme.disabledTextColor || theme.lightBorderColor};
    }
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    overflow: hidden;
`;
